import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../../../../ui/components/Button";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherChevronDown } from "@subframe/core";
import { FeatherUpload } from "@subframe/core";
import { FeatherUploadCloud } from "@subframe/core";
import { FeatherAlertTriangle } from "@subframe/core";

import {
  fetchBankAccounts,
  fetchCreditCardAccounts,
} from "../../data/api/accountsApi";
import {
  uploadStatementExcelApi,
  downloadStatementExcelTemplateApi,
} from "../../data/api/documentsApi";
import { DocumentMapper } from "../../data/mappers/DocumentMapper";
import { validateStatementExcel } from "../../utils/validateStatementExcel";
import { BankAccount } from "../../domain/entities/BankAccount";
import { CreditCardAccount } from "../../domain/entities/CreditCardAccount";
import { DocumentRepositoryImpl } from "../../data/repositories/DocumentRepositoryImpl";
import { UploadStatementUseCase } from "../../domain";
import { SearchableDropdown } from "../../../../shared/presentation/components";
import { kickReductoWorker } from "../../data/api/statementReviewApi";
import StatementReviewDialog from "./StatementReviewDialog";

// Create repository and use case instances
const documentRepository = new DocumentRepositoryImpl();
const uploadStatementUseCase = new UploadStatementUseCase(documentRepository);

interface StatementUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "bank" | "credit";
  onSuccess?: (message: string) => void;
}

const StatementUploadModal: React.FC<StatementUploadModalProps> = ({
  open,
  onOpenChange,
  type,
  onSuccess,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(
    undefined
  );
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<BankAccount | null>(null);
  const [selectedCreditCardAccount, setSelectedCreditCardAccount] =
    useState<CreditCardAccount | null>(null);
  const [uploadFormat, setUploadFormat] = useState<"pdf" | "excel">("pdf");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [creditCardAccounts, setCreditCardAccounts] = useState<
    CreditCardAccount[]
  >([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  // Review-before-push flow: set when a PDF upload was queued for Reducto
  // extraction; keeps the review dialog alive after this modal closes.
  const [reviewJob, setReviewJob] = useState<{
    jobId: string;
    recordId: number;
    filename: string;
    bankLabel: string;
  } | null>(null);

  const isBank = type === "bank";
  const title = isBank
    ? "Upload Bank Statement"
    : "Upload Credit Card Statement";
  const description = isBank
    ? "Select your bank and upload your statement file"
    : "Select your credit card provider and upload your statement file";
  const sourceLabel = isBank ? "Select Bank" : "Select Credit Card Provider";
  const placeholder = isBank
    ? "Choose your bank"
    : "Choose your credit card provider";

  // Fetch bank accounts from API
  const fetchBankAccountsData = useCallback(async () => {
    if (!isBank) return; // Only fetch for bank statements

    try {
      setLoadingAccounts(true);
      const data = await fetchBankAccounts();

      if (data.success && data.data.bank_accounts) {
        // Filter only active bank accounts
        const activeAccounts = data.data.bank_accounts.filter(
          (account) => account.is_active
        );
        setBankAccounts(activeAccounts);
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      // Fallback to empty array if API fails
      setBankAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [isBank]);

  // Fetch credit card accounts from API
  const fetchCreditCardAccountsData = useCallback(async () => {
    if (isBank) return; // Only fetch for credit card statements

    try {
      setLoadingAccounts(true);
      const data = await fetchCreditCardAccounts();

      if (data.success && data.data.credit_card_accounts) {
        // Filter only active credit card accounts
        const activeAccounts = data.data.credit_card_accounts.filter(
          (account) => account.is_active
        );
        setCreditCardAccounts(activeAccounts);
      }
    } catch (error) {
      console.error("Error fetching credit card accounts:", error);
      // Fallback to empty array if API fails
      setCreditCardAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [isBank]);

  // Fetch accounts when modal opens
  useEffect(() => {
    if (open && isBank) {
      fetchBankAccountsData();
    } else if (open && !isBank) {
      fetchCreditCardAccountsData();
    }
  }, [open, isBank, fetchBankAccountsData, fetchCreditCardAccountsData]);

  // Update dropdown width to match trigger button
  useEffect(() => {
    const updateWidth = () => {
      if (triggerButtonRef.current) {
        const width = triggerButtonRef.current.offsetWidth;
        setDropdownWidth(width);
      }
    };

    if (open) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        updateWidth();
      });
    }
  }, [open, selectedBankAccount, selectedCreditCardAccount, loadingAccounts]);

  // Reset form when modal closes or opens
  useEffect(() => {
    if (!open) {
      setUploadFormat("pdf");
      setSelectedBankAccount(null);
      setSelectedCreditCardAccount(null);
      setSelectedFile(null);
      setUploadError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      // Clear error when modal opens
      setUploadError(null);
    }
  }, [open]);

  // Clear selected file when format changes so user picks correct type
  useEffect(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [uploadFormat]);

  // Clear selected file when auto account is selected
  useEffect(() => {
    const isAutoAccount = isBank
      ? selectedBankAccount?.is_auto === true
      : selectedCreditCardAccount?.is_auto === true;

    if (isAutoAccount && selectedFile) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [selectedBankAccount, selectedCreditCardAccount, isBank, selectedFile]);

  const selectedAccountPendingReview = isBank
    ? selectedBankAccount?.pending_review
    : selectedCreditCardAccount?.pending_review;
  const selectedAccountIsLocked =
    selectedAccountPendingReview?.status === "pending" &&
    (selectedAccountPendingReview.action_code === "update" ||
      selectedAccountPendingReview.action_code === "delete");

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent file selection if auto account is selected
    const isAutoAccount = isBank
      ? selectedBankAccount?.is_auto === true
      : selectedCreditCardAccount?.is_auto === true;

    if (isAutoAccount) {
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Clear error when user selects a new file
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // For bank statements, we need selectedBankAccount
    // For credit card statements, we need selectedCreditCardAccount
    if (isBank && !selectedBankAccount) return;
    if (!isBank && !selectedCreditCardAccount) return;

    // Prevent upload if account is auto
    if (selectedAccountIsAuto) {
      setUploadError(
        isBank
          ? "You cannot upload to an auto bank account. Please select a manually added bank account."
          : "You cannot upload to an auto credit card account. Please select a manually added credit card account."
      );
      return;
    }

    if (selectedAccountIsLocked) {
      setUploadError(
        isBank
          ? "This bank account has a pending update/delete request. Uploads are disabled until the review is approved or rejected."
          : "This credit card account has a pending update/delete request. Uploads are disabled until the review is approved or rejected."
      );
      return;
    }

    // Clear any previous errors
    setUploadError(null);

    try {
      if (uploadFormat === "excel") {
        const validation = await validateStatementExcel(selectedFile);
        if (!validation.valid) {
          setUploadError(
            validation.message ||
              "Invalid Excel format. Please download the template and try again."
          );
          return;
        }
      }

      setIsUploading(true);

      const source = isBank ? "bank" : "credit";
      const bankName = isBank
        ? selectedBankAccount?.bank_name || ""
        : selectedCreditCardAccount?.bank_name || "";
      const zohoBankAccountId = isBank
        ? selectedBankAccount?.zoho_bankaccountid || ""
        : selectedCreditCardAccount?.zoho_bankaccountid || "";

      const result =
        uploadFormat === "excel"
          ? DocumentMapper.toUploadResult(
              await uploadStatementExcelApi(
                selectedFile,
                source,
                bankName,
                zohoBankAccountId
              )
            )
          : await uploadStatementUseCase.execute(
              selectedFile,
              source,
              bankName,
              zohoBankAccountId
            );

      if (result.reviewFlow && result.jobId && result.recordId) {
        // PDF went to the Reducto review-before-push flow: start the
        // extraction worker immediately (fire-and-forget; cron is the
        // fallback) and open the review dialog.
        kickReductoWorker();
        const accountLabel = isBank
          ? selectedBankAccount?.bank_name || bankName
          : selectedCreditCardAccount?.bank_name || bankName;
        setReviewJob({
          jobId: result.jobId,
          recordId: result.recordId,
          filename: selectedFile.name,
          bankLabel: accountLabel,
        });
        handleClose();
        return;
      }

      // Call success callback
      const successMessage = `Successfully uploaded ${selectedFile.name}${
        result.jobId ? `. Job ID: ${result.jobId}` : ""
      }`;
      onSuccess?.(successMessage);

      // Close modal on success
      handleClose();
    } catch (error: any) {
      console.error("Upload failed:", error);
      // Server validation errors (e.g. 400 format/row check for Excel) surface here so user can correct file without leaving the modal
      const rawMessage = error?.message || "Upload failed. Please try again.";
      const friendlyMessage = rawMessage.includes(
        "Textract text detection timed out"
      )
        ? "Upload failed. Check your network and try again. If the problem persists, please contact support."
        : rawMessage;
      setUploadError(friendlyMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setUploadFormat("pdf");
    setSelectedBankAccount(null);
    setSelectedCreditCardAccount(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onOpenChange(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      setUploadError(null);
      await downloadStatementExcelTemplateApi();
    } catch (error: any) {
      console.error("Template download failed:", error);
      setUploadError(
        error?.message || "Failed to download template. Please try again."
      );
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Check if selected account is auto
  const selectedAccountIsAuto = isBank
    ? selectedBankAccount?.is_auto === true
    : selectedCreditCardAccount?.is_auto === true;

  return (
    <>
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full max-w-[576px] flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {title}
            </span>
            <span className="text-body font-body text-subtext-color">
              {description}
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            {/* Format choice: PDF or Excel */}
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Format
              </span>
              <div className="flex w-full gap-2">
                <Button
                  variant={
                    uploadFormat === "pdf"
                      ? "brand-primary"
                      : "neutral-secondary"
                  }
                  className="flex-1"
                  onClick={() => setUploadFormat("pdf")}
                >
                  PDF statement
                </Button>
                <Button
                  variant={
                    uploadFormat === "excel"
                      ? "brand-primary"
                      : "neutral-secondary"
                  }
                  className="flex-1"
                  onClick={() => setUploadFormat("excel")}
                >
                  Excel statement
                </Button>
              </div>
            </div>

            {/* Excel format notice and download link */}
            {uploadFormat === "excel" && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-3">
                <span className="text-body font-body text-default-font">
                  We only accept Excel uploads of bank statements in a specific
                  format. If you do not have the format, please{" "}
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    aria-label="Download template"
                    title="Download Excel bank statement template"
                    className="text-body-bold font-body-bold text-brand-600 underline hover:text-brand-700 disabled:opacity-50"
                  >
                    {downloadingTemplate
                      ? "Downloading..."
                      : "Download template"}
                  </button>
                  .
                </span>
              </div>
            )}

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                {sourceLabel}
              </span>
              <SearchableDropdown
                items={(isBank ? bankAccounts : creditCardAccounts) as any}
                loading={loadingAccounts}
                trigger={
                  <Button
                    ref={triggerButtonRef}
                    className="h-10 w-full flex-none justify-between"
                    variant="neutral-secondary"
                    iconRight={<FeatherChevronDown />}
                    disabled={loadingAccounts}
                  >
                    {loadingAccounts
                      ? isBank
                        ? "Loading banks..."
                        : "Loading credit cards..."
                      : isBank && selectedBankAccount
                      ? selectedBankAccount.bank_name
                      : !isBank && selectedCreditCardAccount
                      ? selectedCreditCardAccount.bank_name
                      : placeholder}
                  </Button>
                }
                getSearchableText={(account: any) => account.bank_name}
                renderItem={(account: any) => {
                  const pr = account?.pending_review;
                  const locked =
                    pr?.status === "pending" &&
                    (pr?.action_code === "update" || pr?.action_code === "delete");
                  return locked
                    ? `${account.bank_name} (Locked – pending ${pr.action_code})`
                    : account.bank_name;
                }}
                getItemKey={(account: any) => account.id}
                onSelect={(account: any) => {
                  const pr = account?.pending_review;
                  const locked =
                    pr?.status === "pending" &&
                    (pr?.action_code === "update" || pr?.action_code === "delete");
                  if (locked) {
                    setUploadError(
                      "That account is locked due to a pending update/delete review."
                    );
                    return;
                  }
                  if (isBank) {
                    setSelectedBankAccount(account as BankAccount);
                  } else {
                    setSelectedCreditCardAccount(account as CreditCardAccount);
                  }
                  setUploadError(null);
                }}
                selectedKey={
                  isBank
                    ? selectedBankAccount?.id
                    : selectedCreditCardAccount?.id
                }
                searchPlaceholder={
                  isBank ? "Search bank accounts..." : "Search credit cards..."
                }
                emptyMessage={
                  isBank
                    ? "No bank accounts found"
                    : "No credit card accounts found"
                }
                noResultsMessage="No accounts match your search"
                dropdownClassName={dropdownWidth ? `max-h-65` : "max-h-65"}
              />
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Upload Statement
              </span>
              <div
                className={`flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed px-6 py-8 ${
                  selectedAccountIsAuto
                    ? "border-neutral-300 bg-neutral-100 cursor-not-allowed opacity-50"
                    : "border-brand-600 bg-brand-50 cursor-pointer"
                }`}
                onClick={() => {
                  if (!selectedAccountIsAuto) {
                    document.getElementById("file-input")?.click();
                  }
                }}
              >
                <IconWithBackground
                  size="large"
                  icon={<FeatherUploadCloud />}
                />
                <div className="flex flex-col items-center justify-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font text-center">
                    {selectedFile
                      ? selectedFile.name
                      : "Click to select file or drag to upload"}
                  </span>
                  <span className="text-caption font-caption text-subtext-color text-center">
                    {uploadFormat === "pdf"
                      ? "Supported formats: PDF (Max 25MB)"
                      : "Supported formats: Excel (.xls, .xlsx)"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Auto Account Warning */}
          {selectedAccountIsAuto && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-3">
              <FeatherAlertTriangle className="h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-body-bold font-body-bold text-warning-700">
                  Auto Account Selected
                </span>
                <span className="text-body font-body text-warning-600">
                  {isBank
                    ? "You cannot upload statements to an auto bank account. Please select a manually added bank account."
                    : "You cannot upload statements to an auto credit card account. Please select a manually added credit card account."}
                </span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {uploadError && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
              <FeatherAlertTriangle className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-body-bold font-body-bold text-error-700">
                  Upload Failed
                </span>
                <span className="text-body font-body text-error-600">
                  {uploadError}
                </span>
              </div>
            </div>
          )}

          <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-6">
            <Button
              variant="neutral-tertiary"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              icon={
                isUploading ? (
                  <div className="animate-spin">⏳</div>
                ) : (
                  <FeatherUpload />
                )
              }
              onClick={handleUpload}
              disabled={
                !selectedFile ||
                isUploading ||
                (isBank ? !selectedBankAccount : !selectedCreditCardAccount) ||
                selectedAccountIsAuto
              }
            >
              {isUploading ? "Uploading..." : "Upload Statement"}
            </Button>
          </div>
        </div>
      </DialogLayout>

      {/* Hidden file input */}
      <input
        id="file-input"
        ref={fileInputRef}
        type="file"
        accept={uploadFormat === "pdf" ? ".pdf" : ".xls,.xlsx"}
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={selectedAccountIsAuto}
      />

      {/* Review-before-push flow (PDF statements via Reducto) */}
      {reviewJob && (
        <StatementReviewDialog
          open={!!reviewJob}
          onOpenChange={(isOpen) => {
            if (!isOpen) setReviewJob(null);
          }}
          jobId={reviewJob.jobId}
          recordId={reviewJob.recordId}
          filename={reviewJob.filename}
          bankLabel={reviewJob.bankLabel}
          onFinished={(message) => onSuccess?.(message)}
        />
      )}
    </>
  );
};

export default StatementUploadModal;
